/**
 * HomeAnalytics — modern analytics overview rendered on the dashboard Home tab.
 *
 * Composes a headline KPI strip with per-module section cards. Every section is
 * gated by the client's enabled modules (so `go`/`salam` only see what they
 * have), and the whole block is gated by the `dashboard.analytics` permission.
 */

import React from 'react';
import {useTranslation} from 'react-i18next';
import {LineChart} from 'lucide-react';
import {
  useEnabledModules,
  useClientFeatures
} from '../../../../core/context/ClientContext';
import {useAuthorization} from '../../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../../constants/permissions';
import {
  AppSectionTitle,
  AppSmallText
} from '../../../../Presentation/components/ui/Typography';
import AnalyticsKpiStrip from './AnalyticsKpiStrip';
import InventoryProductsSection from './sections/InventoryProductsSection';
import VisitsSection from './sections/VisitsSection';
import StoresUsersSection from './sections/StoresUsersSection';

const HomeAnalytics: React.FC = () => {
  const {t} = useTranslation();
  const modules = useEnabledModules();
  const {serialsEnabled} = useClientFeatures();
  const {hasPermission} = useAuthorization();

  const inventoryProductsEnabled =
    modules.includes('inventory') || modules.includes('products');
  const visitsEnabled = modules.includes('visits');
  const storesUsersEnabled =
    modules.includes('stores') || modules.includes('users');

  const anyEnabled =
    inventoryProductsEnabled || visitsEnabled || storesUsersEnabled;

  // Keep the plain welcome view for users without analytics access or clients
  // with none of the relevant modules enabled.
  if (!anyEnabled || !hasPermission(PERMISSIONS.DASHBOARD_ANALYTICS)) {
    return null;
  }

  return (
    <section className="space-y-5">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-brand-50 text-primary flex items-center justify-center">
          <LineChart className="w-5 h-5" />
        </span>
        <div>
          <AppSectionTitle className="font-bold text-grayText">
            {t('analytics.overview_title')}
          </AppSectionTitle>
          <AppSmallText className="text-gray-500">
            {t('analytics.overview_subtitle')}
          </AppSmallText>
        </div>
      </div>

      <AnalyticsKpiStrip
        inventoryProductsEnabled={inventoryProductsEnabled}
        visitsEnabled={visitsEnabled}
        storesUsersEnabled={storesUsersEnabled}
      />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-start">
        {inventoryProductsEnabled && (
          <InventoryProductsSection
            enabled={inventoryProductsEnabled}
            serialsEnabled={serialsEnabled}
          />
        )}
        {visitsEnabled && <VisitsSection enabled={visitsEnabled} />}
        {storesUsersEnabled && (
          <StoresUsersSection enabled={storesUsersEnabled} />
        )}
      </div>
    </section>
  );
};

export default HomeAnalytics;
